// Cache bust: 2025-10-27-20:32 - MOBILE CATEGORY DEBUG
import React, { useState, useRef, useEffect } from 'react';
import { Amplify } from 'aws-amplify';
import { Authenticator } from '@aws-amplify/ui-react';
import { getCurrentUser, fetchAuthSession, fetchUserAttributes } from 'aws-amplify/auth';
import '@aws-amplify/ui-react/styles.css';
import { awsConfig } from './aws-config';
import SubscriptionModal from './components/SubscriptionModal';
import './components/SubscriptionModal.css';
import './App.css';
import './ai-insights.css';

// Capacitor imports for native features
import { Haptics, ImpactStyle } from '@capacitor/haptics';

Amplify.configure(awsConfig);

function App() {
  const [isRecording, setIsRecording] = useState(false);
  // Auto-populate email field for better UX
  useEffect(() => {
    const populateEmailField = () => {
      const savedEmail = localStorage.getItem('lastEmail');
      if (savedEmail) {
        // Try to find the username/email input field
        const emailInput = document.querySelector('input[name="username"], input[type="email"], input[placeholder*="email" i]');
        if (emailInput && !emailInput.value) {
          emailInput.value = savedEmail;
          console.log('Auto-populated email field:', savedEmail);
        }
      }
    };

    // Run immediately and also after a short delay to catch Amplify form rendering
    populateEmailField();
    const timer = setTimeout(populateEmailField, 500);
    
    return () => clearTimeout(timer);
  }, []);

  const [notes, setNotes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [liveTranscript, setLiveTranscript] = useState('');
  const [authToken, setAuthToken] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [userFirstName, setUserFirstName] = useState('');
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [mediaStream, setMediaStream] = useState(null);
  const [microphoneError, setMicrophoneError] = useState(null);
  const [aiInsights, setAiInsights] = useState({});
  const [expandedAiInsights, setExpandedAiInsights] = useState({});
  const [aiProcessing, setAiProcessing] = useState({});
  const [expandedTranscripts, setExpandedTranscripts] = useState({});
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  // Category definitions with icons and colors
  const CATEGORIES = {
    'Meeting Notes': { icon: '📝', color: '#4A90E2' },
    'Action Items': { icon: '📋', color: '#E94B3C' },
    'Ideas & Planning': { icon: '💡', color: '#F5A623' },
    'Progress Updates': { icon: '📊', color: '#7ED321' },
    'Reminders': { icon: '🎯', color: '#9013FE' },
    'Personal Notes': { icon: '📄', color: '#50E3C2' },
    'Custom': { icon: '🏷️', color: '#BD10E0' }
  };
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recognitionRef = useRef(null);
  const pendingAudioBlobRef = useRef(null);
  const finalTranscriptRef = useRef('');

  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(isDarkMode));
    document.documentElement.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');
    
    // Update status bar for iOS - only for native apps
    // StatusBar functionality removed for web compatibility
  }, [isDarkMode]);

  useEffect(() => {
    // Notes will be loaded from cloud when user authenticates
    // No longer loading from localStorage on startup to prevent data leakage
    
  }, []);

  useEffect(() => {
    // Load cloud notes when auth token becomes available
    if (authToken && currentUser) {
      loadNotesFromCloud();
    }
  }, [authToken, currentUser]);

  // Cleanup media stream on unmount
  useEffect(() => {
    return () => {
      if (mediaStream) {
        mediaStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [mediaStream]);

  // Function to refresh auth token
  const refreshAuthToken = async () => {
    try {
      const session = await fetchAuthSession({ forceRefresh: true });
      const token = session.tokens?.idToken?.toString();
      console.log('Token refreshed:', !!token);
      setAuthToken(token);
      return token;
    } catch (error) {
      console.error('Failed to refresh token:', error);
      return null;
    }
  };

  // Function to make authenticated requests with token refresh
  const makeAuthenticatedRequest = async (url, options = {}) => {
    let token = authToken;
    
    // Try the request with current token
    let response = await fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        'Authorization': `Bearer ${token}`,
      }
    });
    
    // If unauthorized, try to refresh token and retry
    if (response.status === 401) {
      console.log('Token expired, refreshing...');
      token = await refreshAuthToken();
      if (token) {
        response = await fetch(url, {
          ...options,
          headers: {
            ...options.headers,
            'Authorization': `Bearer ${token}`,
          }
        });
      }
    }
    
    return response;
  };

  const handleAuthUser = async (user) => {
    if (user && !currentUser) {
      setCurrentUser(user);
      console.log('User authenticated:', user.username);
      
      // Track Google Ads conversion for new sign-up
      if (typeof gtag !== 'undefined') {
        gtag('event', 'conversion', {
          'send_to': 'AW-757187431/5_8_CJ_FkLsbEOeGh-kC',
          'value': 19.99,
          'currency': 'USD'
        });
        console.log('Google Ads conversion tracked for new sign-up');
      }
      
      // Clear any existing notes when new user logs in
      setNotes([]);
      localStorage.removeItem('voiceNotes');
      
      try {
        const session = await fetchAuthSession();
        const token = session.tokens?.idToken?.toString();
        console.log('Token retrieved:', !!token);
        setAuthToken(token);

        // Try to get user attributes first
        try {
          const attributes = await fetchUserAttributes();
          const firstName = attributes.given_name;
          const lastName = attributes.family_name;
          
          if (firstName && lastName) {
            setUserFirstName(`${firstName} ${lastName}`);
            console.log('User full name from attributes:', `${firstName} ${lastName}`);
            return;
          } else if (firstName) {
            setUserFirstName(firstName);
            console.log('User first name from attributes:', firstName);
            return;
          }
        } catch (attrError) {
          console.log('Could not fetch user attributes:', attrError);
        }

        // Fallback: extract name from email or use username
        const email = user.signInDetails?.loginId || user.username;
        if (email && email.includes('@')) {
          const emailPart = email.split('@')[0];
          const firstName = emailPart.charAt(0).toUpperCase() + emailPart.slice(1);
          setUserFirstName(firstName);
          console.log('User first name from email:', firstName);
        } else {
          setUserFirstName(user.username);
        }
      } catch (error) {
        console.log('Token/attributes error:', error);
        setUserFirstName(user.username); // Final fallback
      }
    } else if (!user && currentUser) {
      setCurrentUser(null);
      setAuthToken(null);
      setUserFirstName('');
      // SECURITY FIX: Clear ALL localStorage when user logs out to prevent data leakage on shared devices
      setNotes([]);
      localStorage.clear(); // Clear everything to prevent cross-user data leakage
    }
  };

  const loadNotesFromCloud = async () => {
    if (!authToken) {
      console.log('No auth token, skipping cloud load');
      return;
    }
    
    try {
      console.log('Loading notes from cloud...');
      
      // Clear existing notes first to ensure fresh start
      setNotes([]);
      
      const response = await fetch(`${import.meta.env.VITE_API_URL}/notes`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        }
      });
      
      console.log('Cloud response status:', response.status);
      
      if (response.ok) {
        const cloudNotes = await response.json();
        console.log('Loaded notes from cloud:', cloudNotes);
        console.log('🔍 First note structure:', cloudNotes[0]);
        
        if (cloudNotes && cloudNotes.length > 0) {
          setNotes(cloudNotes);
          localStorage.setItem('voiceNotes', JSON.stringify(cloudNotes));
          console.log('Successfully loaded', cloudNotes.length, 'notes from cloud');
          
          // Load AI insights for these notes
          await loadAiInsightsFromCloud(cloudNotes);
        } else {
          console.log('No notes found in cloud');
          // Clear localStorage if no cloud notes
          localStorage.removeItem('voiceNotes');
        }
      } else {
        console.error('Failed to load from cloud:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Error loading notes from cloud:', error);
    }
  };

  const loadAiInsightsFromCloud = async (notes) => {
    if (!authToken || !notes || notes.length === 0) return;
    
    try {
      console.log('🤖 Loading AI insights from cloud...');
      
      try {
        const response = await fetch(`${import.meta.env.VITE_API_URL}/ai`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`,
          }
        });
        
        if (response.ok) {
          const aiData = await response.json();
          const insights = {};
          
          // Map AI insights to notes
          aiData.forEach(item => {
            insights[item.noteId] = item.aiInsights;
          });
          
          if (Object.keys(insights).length > 0) {
            setAiInsights(insights);
            console.log('🤖 Loaded AI insights for', Object.keys(insights).length, 'notes');
          }
        } else {
          console.log('Failed to load AI insights:', response.status);
        }
      } catch (error) {
        console.error('Error loading AI insights:', error);
      }
    } catch (error) {
      console.error('Error loading AI insights:', error);
    }
  };

  const startRecording = async () => {
    try {
      console.log('Starting recording...');
      setLiveTranscript('');
      setMicrophoneError(null);
      
      // Add haptic feedback for iOS
      try {
        await Haptics.impact({ style: ImpactStyle.Medium });
      } catch (e) {
        // Web environment, ignore
      }
      
      // Check if browser supports getUserMedia
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        alert('Your browser does not support audio recording. Please use Chrome, Firefox, or Safari.');
        return;
      }

      // Get or reuse media stream (iOS Safari fix)
      let stream = mediaStream;
      
      // Only request new stream if we don't have one or it's inactive
      if (!stream || !stream.active || stream.getTracks().some(track => track.readyState === 'ended')) {
        console.log('Requesting new microphone access...');
        try {
          stream = await navigator.mediaDevices.getUserMedia({ 
            audio: {
              echoCancellation: true,
              noiseSuppression: true,
              sampleRate: 44100
            } 
          });
          setMediaStream(stream);
          console.log('New media stream obtained');
        } catch (error) {
          console.error('getUserMedia error:', error);
          handleMicrophoneError(error);
          return;
        }
      } else {
        console.log('Reusing existing microphone stream');
      }

      // Start real-time speech recognition FIRST
      if (window.SpeechRecognition || window.webkitSpeechRecognition) {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        recognitionRef.current = new SpeechRecognition();
        
        recognitionRef.current.continuous = true;
        recognitionRef.current.interimResults = true;
        recognitionRef.current.lang = 'en-US';

        recognitionRef.current.onstart = () => {
          console.log('Speech recognition started successfully');
        };

        
        recognitionRef.current.onresult = (event) => {
          console.log('🎤 Speech recognition result event:', event);
          let interimTranscript = '';
          let finalTranscript = '';
          let hasFinalResult = false;

          for (let i = event.resultIndex; i < event.results.length; i++) {
            const transcript = event.results[i][0].transcript;
            console.log('🎤 Transcript part:', transcript, 'isFinal:', event.results[i].isFinal, 'length:', transcript.length);
            
            if (event.results[i].isFinal) {
              finalTranscript += transcript + ' ';
              hasFinalResult = true;
            } else {
              interimTranscript += transcript;
            }
          }

          const fullTranscript = finalTranscript + interimTranscript;
          console.log('🎤 Setting live transcript to:', fullTranscript, 'length:', fullTranscript.length);
          setLiveTranscript(fullTranscript);

          // Store transcript continuously
          if (fullTranscript.trim().length > 0) {
            finalTranscriptRef.current = fullTranscript.trim();
            console.log('💾 Stored transcript in ref (continuous):', finalTranscriptRef.current.length, 'chars');
          }

          // If we have a final result and there's a pending audio blob, save the note
          if (hasFinalResult && pendingAudioBlobRef.current) {
            console.log('🎤 Final transcript received, saving note with:', fullTranscript, 'length:', fullTranscript.length);
            
            saveNoteWithTranscript(pendingAudioBlobRef.current, fullTranscript);
            pendingAudioBlobRef.current = null;
            finalTranscriptRef.current = '';
          }
        };

        recognitionRef.current.onerror = (event) => {
          console.error('Speech recognition error:', event.error);
          
          // Don't show alert for 'aborted' errors - these are normal when stopping
          if (event.error !== 'aborted') {
            // Only show alerts for actual errors that users need to know about
            if (event.error === 'not-allowed') {
              alert('Speech recognition access denied. Please allow microphone access.');
            } else if (event.error === 'no-speech') {
              console.log('No speech detected - this is normal');
            } else if (event.error === 'network') {
              alert('Network error during speech recognition. Please check your connection.');
            } else {
              console.log('Speech recognition error (non-critical):', event.error);
            }
          }
        };

        recognitionRef.current.onend = () => {
          console.log('Speech recognition ended');
        };

        console.log('Starting speech recognition...');
        recognitionRef.current.start();
      } else {
        console.log('Speech recognition not available');
        alert('Speech recognition not available in this browser. Please use Chrome.');
      }

      // Then start audio recording for playback using the stream we have
      console.log('Using media stream for recording:', stream);
      
      console.log('Got media stream:', stream);
      
      if (!window.MediaRecorder) {
        alert('MediaRecorder is not supported in your browser.');
        return;
      }

      mediaRecorderRef.current = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/mp4') ? 'audio/mp4' : 
                  MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/wav'
      });
      audioChunksRef.current = [];
      
      // Store stream reference in MediaRecorder for cleanup
      mediaRecorderRef.current._stream = stream;

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = () => {
        console.log('Recording stopped');
        if (audioChunksRef.current.length > 0) {
          const audioBlob = new Blob(audioChunksRef.current, { 
            type: mediaRecorderRef.current.mimeType 
          });
          
          // Store the audio blob and wait for final transcript
          console.log('Storing audio blob, waiting for final transcript...');
          pendingAudioBlobRef.current = audioBlob;
          
          // Fallback: if no final transcript comes within 3 seconds, save anyway
          setTimeout(() => {
            console.log('Timeout check: pendingAudioBlob exists?', !!pendingAudioBlobRef.current);
            console.log('Timeout check: finalTranscript stored?', finalTranscriptRef.current);
            if (pendingAudioBlobRef.current === audioBlob) {
              console.log('Timeout: saving note with stored transcript:', finalTranscriptRef.current);
              saveNoteWithTranscript(audioBlob, finalTranscriptRef.current || liveTranscript);
              pendingAudioBlobRef.current = null;
              finalTranscriptRef.current = '';
            }
          }, 3000);
        }
        
        // Clean up media stream tracks - try both references
        let streamToClean = mediaStream || (mediaRecorderRef.current && mediaRecorderRef.current._stream);
        
        if (streamToClean && streamToClean.getTracks) {
          const trackCount = streamToClean.getTracks().length;
          streamToClean.getTracks().forEach(track => {
            track.stop();
          });
          setMediaStream(null);
        }
      };

      mediaRecorderRef.current.start(1000);
      setIsRecording(true);
      console.log('Recording started successfully');
      
    } catch (error) {
      console.error('Error starting recording:', error);
      handleMicrophoneError(error);
    }
  };

  const handleMicrophoneError = (error) => {
    console.error('Microphone error:', error);
    setMicrophoneError(error);
    
    if (error.name === 'NotAllowedError') {
      // Detect iOS
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      
      if (isIOS) {
        alert(`Microphone access issue detected on iOS Safari.

Try these solutions:
1. 🔄 Refresh this page
2. ⚙️ Check Settings > Safari > Microphone  
3. 🔄 Close Safari completely and reopen
4. 📱 Restart your device (last resort)

This is a known iOS Safari bug we're working to resolve.`);
      } else {
        alert('Microphone access denied. Please allow microphone access in your browser settings and try again.');
      }
    } else if (error.name === 'NotFoundError') {
      alert('No microphone found. Please connect a microphone and try again.');
    } else {
      alert('Error starting recording: ' + error.message + '\n\nTry refreshing the page if the problem persists.');
    }
  };

  const resetMicrophone = () => {
    console.log('Resetting microphone...');
    
    // Stop existing stream
    if (mediaStream) {
      mediaStream.getTracks().forEach(track => track.stop());
      setMediaStream(null);
    }
    
    // Clear error
    setMicrophoneError(null);
    
    // Refresh the page to reset Safari state
    window.location.reload();
  };

  const stopRecording = () => {
    console.log('Stopping recording...');
    
    // Add haptic feedback for iOS
    try {
      Haptics.impact({ style: ImpactStyle.Light });
    } catch (e) {
      // Web environment, ignore
    }
    
    // Stop speech recognition
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    
    // Stop media recording
    if (mediaRecorderRef.current && isRecording) {
      if (mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
        console.log('MediaRecorder stopped');
      }
    }
    
    setIsRecording(false);
    
    // Note: Media stream cleanup moved to MediaRecorder onstop handler
    // to prevent cutting off recordings before they're fully processed
  };

  
  const saveNoteWithTranscript = async (audioBlob, transcript) => {
    console.log('📝 === SAVE NOTE WITH TRANSCRIPT DEBUG ===');
    console.log('📝 saveNoteWithTranscript called with transcript:', transcript);
    console.log('📝 transcript length:', transcript.length);
    console.log('📝 transcript trimmed:', transcript.trim());
    
    // Use the provided transcript, or fallback if empty
    const initialText = transcript.trim() || `Voice note recorded at ${new Date().toLocaleTimeString()} - Processing...`;
    const noteId = Date.now().toString();
    
    console.log('📝 Initial text being saved:', initialText, 'length:', initialText.length);
    console.log('📝 =======================================');
    
    // Create temporary note first (for immediate feedback)
    const tempNote = {
      id: noteId,
      text: initialText,
      timestamp: new Date().toISOString(),
      audioUrl: URL.createObjectURL(audioBlob),
      syncing: true,
      transcribing: true, // FIXED: Always start AWS Transcribe for complete accuracy
      transcribeCompleted: false
    };
    
    const updatedNotes = [tempNote, ...notes];
    setNotes(updatedNotes);
    localStorage.setItem('voiceNotes', JSON.stringify(updatedNotes));
    setLiveTranscript('');

    try {
      // Convert audio blob to base64 (chunked to avoid stack overflow)
      const arrayBuffer = await audioBlob.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);
      
      // Convert in chunks to avoid stack overflow
      let binaryString = '';
      const chunkSize = 8192;
      for (let i = 0; i < uint8Array.length; i += chunkSize) {
        const chunk = uint8Array.slice(i, i + chunkSize);
        binaryString += String.fromCharCode.apply(null, chunk);
      }
      const base64Audio = btoa(binaryString);

      console.log('🔍 Attempting to save to cloud:', `${import.meta.env.VITE_API_URL}/notes`);
      
      const response = await fetch(`${import.meta.env.VITE_API_URL}/notes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          noteId,
          text: initialText,
          timestamp: tempNote.timestamp,
          audioData: base64Audio,
          audioType: audioBlob.type
        })
      });

      console.log('🔍 Cloud save response:', response.status, response.statusText);
      
      if (response.ok) {
        const result = await response.json();
        
        // Update note with permanent audio URL
        const finalNote = {
          ...tempNote,
          audioUrl: result.audioUrl || tempNote.audioUrl,
          syncing: false,
          synced: true
        };
        
        const finalNotes = updatedNotes.map(note => 
          note.id === noteId ? finalNote : note
        );
        
        setNotes(finalNotes);
        localStorage.setItem('voiceNotes', JSON.stringify(finalNotes));
        console.log('Note saved to cloud successfully');
        
        // FIXED: Always start AWS Transcribe for complete accuracy
        console.log('Starting AWS Transcribe for complete transcription...');
        const audioUrl = result.audioUrl;
        
        // Force cache bust: 20251019-2157
        const requestBody = {
          audioUrl: audioUrl
        };
        
        console.log('🔍 TRANSCRIBE REQUEST:', {
          url: `${import.meta.env.VITE_API_URL}/transcribe`,
          method: 'POST',
          body: requestBody,
          audioUrl
        });
        
        const transcribeResponse = await fetch(`${import.meta.env.VITE_API_URL}/transcribe`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify(requestBody),
        });
        
        console.log('🔍 TRANSCRIBE RESPONSE:', {
          status: transcribeResponse.status,
          statusText: transcribeResponse.statusText,
          headers: Object.fromEntries(transcribeResponse.headers.entries())
        });
        
        const responseText = await transcribeResponse.text();
        console.log('🔍 RESPONSE BODY:', responseText);

        if (transcribeResponse.ok) {
          const transcribeResult = JSON.parse(responseText);
          console.log('Transcribe job started:', transcribeResult.jobId);
          
          // Validate jobId before polling
          if (transcribeResult.jobId) {
            // Start polling for results
            pollTranscription(transcribeResult.jobId, noteId);
          } else {
            console.error('No jobId returned from transcribe service');
            setNotes(prevNotes => prevNotes.map(note => 
              note.id === noteId 
                ? { ...note, transcribing: false }
                : note
            ));
          }
        } else {
          console.error('Failed to start transcribe job');
          // Remove transcribing status if AWS Transcribe fails
          setNotes(prevNotes => prevNotes.map(note => 
            note.id === noteId 
              ? { ...note, transcribing: false }
              : note
          ));
        }
      } else {
        throw new Error('Failed to save to cloud');
      }
    } catch (error) {
      console.error('Error saving to cloud:', error);
      
      // Store audio data permanently for retry
      const audioArrayBuffer = await audioBlob.arrayBuffer();
      const localNote = {
        ...tempNote,
        syncing: false,
        syncError: true,
        audioData: Array.from(new Uint8Array(audioArrayBuffer)),
        audioType: audioBlob.type
      };
      
      const localNotes = updatedNotes.map(note => 
        note.id === noteId ? localNote : note
      );
      
      setNotes(localNotes);
      localStorage.setItem('voiceNotes', JSON.stringify(localNotes));
    }
  };

  
  const pollTranscription = async (jobId, noteId) => {
    console.log('🤖 Starting transcription polling for job:', jobId);
    
    // Validate inputs
    if (!jobId || jobId === 'undefined') {
      console.error('🤖 Invalid jobId for polling:', jobId);
      setNotes(prevNotes => prevNotes.map(note => 
        note.id === noteId 
          ? { ...note, transcribing: false, transcribeError: true }
          : note
      ));
      return;
    }
    
    if (!authToken) {
      console.error('🤖 No auth token available for polling');
      setNotes(prevNotes => prevNotes.map(note => 
        note.id === noteId 
          ? { ...note, transcribing: false, transcribeError: true }
          : note
      ));
      return;
    }
    
    const maxAttempts = 30;
    let attempts = 0;
    
    const poll = async () => {
      try {
        console.log('🤖 Polling attempt', attempts + 1, 'for job:', jobId);
        const response = await fetch(`${import.meta.env.VITE_API_URL}/transcribe/${jobId}`, {
          headers: {
            'Authorization': `Bearer ${authToken}`
          }
        });
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const result = await response.json();
        
        console.log('🤖 Transcribe status:', result.status, 'for job:', jobId);
        console.log('🤖 Full transcribe result:', JSON.stringify(result, null, 2));
        
        if (result.status === 'COMPLETED' && result.transcript) {
          console.log('🤖 AWS Transcribe completed, fetching transcript from:', result.transcript);
          
          try {
            // Fetch the actual transcript from S3 URL
            const transcriptResponse = await fetch(result.transcript);
            const transcriptData = await transcriptResponse.json();
            
            // Extract text from AWS Transcribe JSON format
            const transcriptText = transcriptData.results?.transcripts?.[0]?.transcript || '';
            console.log('🤖 AWS Transcribe final text:', transcriptText, 'length:', transcriptText.length);
            
            if (transcriptText) {
              // Update the note with improved transcript
              setNotes(prevNotes => prevNotes.map(note => 
                note.id === noteId 
                  ? { 
                      ...note, 
                      text: transcriptText,
                      transcribing: false,
                      transcribeCompleted: true
                    }
                  : note
              ));
              
              // Update localStorage
              const updatedNotes = notes.map(note => 
                note.id === noteId 
                  ? { 
                      ...note, 
                      text: transcriptText,
                      transcribing: false,
                      transcribeCompleted: true
                    }
                  : note
              );
              localStorage.setItem('voiceNotes', JSON.stringify(updatedNotes));
              
              // Save transcribed text to DynamoDB
              try {
                const noteToUpdate = updatedNotes.find(note => note.id === noteId);
                if (noteToUpdate) {
                  await fetch(`${import.meta.env.VITE_API_URL}/notes`, {
                    method: 'PUT',
                    headers: {
                      'Content-Type': 'application/json',
                      'Authorization': `Bearer ${authToken}`
                    },
                    body: JSON.stringify({
                      noteId: noteToUpdate.id,
                      text: transcriptText,
                      timestamp: noteToUpdate.timestamp,
                      audioUrl: noteToUpdate.audioUrl
                    })
                  });
                  console.log('✅ Transcribed text saved to DynamoDB');
                }
              } catch (error) {
                console.error('❌ Failed to save transcribed text to DynamoDB:', error);
              }
              
              return; // Exit polling loop
            }
          } catch (transcriptError) {
            console.error('❌ Error fetching transcript from S3:', transcriptError);
          }
        } else if (result.status === 'IN_PROGRESS' && attempts < maxAttempts) {
          attempts++;
          setTimeout(poll, 3000); // Poll every 3 seconds
        } else if (result.status === 'FAILED') {
          console.log('Transcribe job failed');
          setNotes(prevNotes => prevNotes.map(note => 
            note.id === noteId 
              ? { ...note, transcribing: false, transcribeError: true }
              : note
          ));
        } else if (attempts >= maxAttempts) {
          console.log('Max polling attempts reached');
          setNotes(prevNotes => prevNotes.map(note => 
            note.id === noteId 
              ? { ...note, transcribing: false, transcribeError: true }
              : note
          ));
        }
      } catch (error) {
        console.error('Polling error:', error);
        attempts++;
        
        // Circuit breaker: stop after 5 consecutive failures or max attempts
        if (attempts >= maxAttempts || attempts >= 5) {
          console.log('Stopping polling due to repeated failures');
          setNotes(prevNotes => prevNotes.map(note => 
            note.id === noteId 
              ? { ...note, transcribing: false, transcribeError: true }
              : note
          ));
          return;
        }
        
        setTimeout(poll, 3000);
      }
    };
    
    poll();
  };

  const deleteNote = async (id) => {
    // Remove from local state immediately
    const updatedNotes = notes.filter(note => note.id !== id);
    setNotes(updatedNotes);
    localStorage.setItem('voiceNotes', JSON.stringify(updatedNotes));

    // Also delete from cloud
    try {
      await fetch(`${import.meta.env.VITE_API_URL}/notes/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${authToken}`,
        }
      });
      console.log('Note deleted from cloud');
    } catch (error) {
      console.error('Error deleting from cloud:', error);
    }
  };

  const downloadMarkdown = (note) => {
    const date = new Date(note.timestamp);
    const markdown = `# Meeting Recording

**Date:** ${date.toLocaleDateString()}
**Time:** ${date.toLocaleTimeString()}
**Duration:** ${note.duration || 'N/A'}

## Transcript

${note.text}

## Notes
- Recording ID: ${note.id}
- Created: ${date.toISOString()}

---
*Generated by MyVoiceMe - Your Personal Meeting Assistant*`;

    // Check if mobile device
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    
    if (isMobile) {
      // For mobile: create proper HTML document for iOS Safari view/download options
      const newWindow = window.open('', '_blank');
      if (newWindow) {
        newWindow.document.write(`
          <html>
            <head>
              <title>recording-${note.id}.md</title>
              <meta name="viewport" content="width=device-width, initial-scale=1.0">
              <style>
                body { 
                  font-family: -apple-system, BlinkMacSystemFont, sans-serif; 
                  padding: 0; 
                  margin: 0;
                  line-height: 1.6; 
                  background: #f8f9fa;
                }
                .header {
                  background: #3498db;
                  color: white;
                  padding: 15px 20px;
                  display: flex;
                  justify-content: space-between;
                  align-items: center;
                  position: sticky;
                  top: 0;
                  z-index: 100;
                }
                .back-btn {
                  background: rgba(255,255,255,0.2);
                  border: 1px solid rgba(255,255,255,0.3);
                  color: white;
                  padding: 8px 16px;
                  border-radius: 6px;
                  font-size: 14px;
                  cursor: pointer;
                  text-decoration: none;
                }
                .back-btn:hover {
                  background: rgba(255,255,255,0.3);
                }
                .content {
                  background: white;
                  margin: 20px;
                  padding: 20px;
                  border-radius: 8px;
                  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                  min-height: calc(100vh - 120px);
                  overflow: visible;
                }
                pre { 
                  white-space: pre-wrap; 
                  word-wrap: break-word; 
                  font-family: -apple-system, BlinkMacSystemFont, sans-serif;
                  margin: 0;
                  max-height: none !important;
                  overflow: visible !important;
                  height: auto !important;
                }
                .title {
                  font-size: 18px;
                  font-weight: 600;
                }
              </style>
            </head>
            <body>
              <div class="header">
                <div class="title">📝 Markdown View</div>
                <button class="back-btn" onclick="window.close()">← Back to App</button>
              </div>
              <div class="content">
                <pre>${markdown.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</pre>
              </div>
            </body>
          </html>
        `);
        newWindow.document.close();
      }
    } else {
      // For desktop: direct download
      const blob = new Blob([markdown], { type: 'text/markdown' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `recording-${note.id}.md`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  const toggleTranscriptExpansion = (noteId, event) => {
    // Prevent event bubbling and default behavior (iOS fix)
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    setExpandedTranscripts(prev => ({
      ...prev,
      [noteId]: !prev[noteId]
    }));
  };

  const triggerTranscriptionForNote = async (note) => {
    console.log('🔄 Triggering transcription for note:', note.id);
    
    if (!note.audioUrl) {
      console.error('🔄 No audio URL available for transcription');
      return;
    }
    
    // Set transcribing state
    setNotes(prevNotes => prevNotes.map(n => 
      n.id === note.id 
        ? { ...n, transcribing: true, transcribeCompleted: false }
        : n
    ));
    
    try {
      const requestBody = { audioUrl: note.audioUrl };
      
      const transcribeResponse = await fetch(`${import.meta.env.VITE_API_URL}/transcribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify(requestBody)
      });
      
      if (transcribeResponse.ok) {
        const transcribeResult = await transcribeResponse.json();
        console.log('🔄 Transcription job created:', transcribeResult.jobId);
        
        if (transcribeResult.jobId) {
          pollTranscription(transcribeResult.jobId, note.id);
        }
      } else {
        console.error('🔄 Failed to create transcription job:', transcribeResponse.status);
        setNotes(prevNotes => prevNotes.map(n => 
          n.id === note.id 
            ? { ...n, transcribing: false, transcribeError: true }
            : n
        ));
      }
    } catch (error) {
      console.error('🔄 Error triggering transcription:', error);
      setNotes(prevNotes => prevNotes.map(n => 
        n.id === note.id 
          ? { ...n, transcribing: false, transcribeError: true }
          : n
      ));
    }
  };

  const retrySync = async (note) => {
    console.log('🔄 Retrying sync for note:', note.id);
    
    // Set syncing state
    setNotes(prevNotes => prevNotes.map(n => 
      n.id === note.id 
        ? { ...n, syncing: true, syncError: false }
        : n
    ));
    
    try {
      // Recreate audio blob from stored data or audioUrl
      let audioBlob = null;
      
      // Use stored audio data first (reliable)
      if (note.audioData && note.audioType) {
        audioBlob = new Blob([new Uint8Array(note.audioData)], {type: note.audioType});
      } else if (note.audioUrl && note.audioUrl.startsWith('blob:')) {
        const response = await fetch(note.audioUrl);
        audioBlob = await response.blob();
      }
      
      if (!audioBlob) {
        throw new Error('No audio data available for retry');
      }
      
      // Convert audio blob to base64
      const arrayBuffer = await audioBlob.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);
      
      let binaryString = '';
      const chunkSize = 8192;
      for (let i = 0; i < uint8Array.length; i += chunkSize) {
        const chunk = uint8Array.slice(i, i + chunkSize);
        binaryString += String.fromCharCode.apply(null, chunk);
      }
      const base64Audio = btoa(binaryString);
      
      // Retry cloud save
      const response = await makeAuthenticatedRequest(`${import.meta.env.VITE_API_URL}/notes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          noteId: note.id,
          text: note.text,
          timestamp: note.timestamp,
          audioData: base64Audio,
          audioType: audioBlob.type
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log('🔄 Full retry sync response:', result);
        
        // Check if we have a complete transcript from the response
        const hasCompleteTranscript = result.transcriptText && result.transcriptText.length > note.text.length;
        
        // Update note as synced and use complete transcript if available
        setNotes(prevNotes => prevNotes.map(n => 
          n.id === note.id 
            ? { 
                ...n, 
                syncing: false, 
                synced: true, 
                syncError: false,
                audioUrl: result.audioUrl || n.audioUrl,
                text: hasCompleteTranscript ? result.transcriptText : n.text,
                transcribing: false,
                transcribeCompleted: hasCompleteTranscript
              }
            : n
        ));
        
        // Update localStorage with the complete transcript
        if (hasCompleteTranscript) {
          const updatedNotes = notes.map(n => 
            n.id === note.id 
              ? { 
                  ...n, 
                  text: result.transcriptText,
                  audioUrl: result.audioUrl || n.audioUrl,
                  synced: true,
                  transcribeCompleted: true
                }
              : n
          );
          localStorage.setItem('voiceNotes', JSON.stringify(updatedNotes));
          console.log('🔄 Updated note with complete transcript from retry sync:', result.transcriptText.substring(0, 100) + '...');
        }
        
        console.log('🔄 Retry sync successful for note:', note.id);
        
        // Only start transcription polling if we don't have a complete transcript and there's a job ID
        if (result.jobId && !hasCompleteTranscript) {
          console.log('🔄 Starting transcription polling for job:', result.jobId);
          pollTranscription(result.jobId, note.id);
        } else if (!result.jobId && !hasCompleteTranscript) {
          // If no transcribe job was created during retry and no complete transcript, trigger transcription manually
          console.log('🔄 No transcribe job from retry, triggering transcription manually');
          
          // Trigger transcription for the synced note using the updated audioUrl
          const syncedNote = {
            ...note,
            audioUrl: result.audioUrl || note.audioUrl
          };
          
          if (syncedNote.audioUrl) {
            triggerTranscriptionForNote(syncedNote);
          }
        } else if (hasCompleteTranscript) {
          console.log('🔄 Complete transcript received from retry sync, no further transcription needed');
        }
      } else {
        throw new Error('Retry sync failed');
      }
    } catch (error) {
      console.error('🔄 Retry sync error:', error);
      
      // Set back to error state
      setNotes(prevNotes => prevNotes.map(n => 
        n.id === note.id 
          ? { ...n, syncing: false, syncError: true }
          : n
      ));
    }
  };

  const truncateTranscript = (text, noteId) => {
    if (!text) return '';
    
    // Approximate 1 minute of speech = ~150-200 words = ~400 characters
    const maxLength = 400;
    
    if (text.length <= maxLength) {
      return text;
    }
    
    // If transcript is expanded, show full text
    if (expandedTranscripts[noteId]) {
      return text;
    }
    
    // Find a good break point (end of sentence or word)
    let truncated = text.substring(0, maxLength);
    const lastPeriod = truncated.lastIndexOf('.');
    const lastSpace = truncated.lastIndexOf(' ');
    
    if (lastPeriod > maxLength - 100) {
      truncated = truncated.substring(0, lastPeriod + 1);
    } else if (lastSpace > maxLength - 50) {
      truncated = truncated.substring(0, lastSpace);
    }
    
    return truncated + '...';
  };

  const processWithAI = async (note) => {
    console.log('🤖 Processing AI for note:', note.id);
    
    // Set processing state
    setAiProcessing(prev => ({
      ...prev,
      [note.id]: true
    }));
    
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/ai`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          noteId: note.id,
          text: note.text,
          action: 'summarize'
        })
      });
      
      console.log('🤖 AI response status:', response.status);
      
      if (response.ok) {
        const result = await response.json();
        console.log('🤖 AI result:', result);
        setAiInsights(prev => ({
          ...prev,
          [note.id]: result.aiInsights || result.insights || result.summary || result
        }));
        setExpandedAiInsights(prev => ({
          ...prev,
          [note.id]: true
        }));
        console.log('🤖 AI insights updated for note:', note.id);
      } else {
        console.error('🤖 AI processing failed:', response.status, response.statusText);
        alert('AI processing failed. Please try again.');
      }
    } catch (error) {
      console.error('🤖 AI processing error:', error);
      alert('AI processing failed. Please try again.');
    } finally {
      // Clear processing state
      setAiProcessing(prev => ({
        ...prev,
        [note.id]: false
      }));
    }
  };

  const toggleAiInsights = (noteId) => {
    setExpandedAiInsights(prev => ({
      ...prev,
      [noteId]: !prev[noteId]
    }));
  };

  const shareNote = async (note) => {
    console.log('Sharing note:', note.id);
    const date = new Date(note.timestamp);
    const text = `Meeting Recording - ${date.toLocaleDateString()}

${note.text}

Generated by MyVoiceMe`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Meeting Recording',
          text: text
        });
      } catch (err) {
        console.log('Share cancelled');
      }
    } else {
      const smsUrl = `sms:?body=${encodeURIComponent(text)}`;
      window.open(smsUrl, '_blank');
    }
  };

  const shareAiInsights = async (noteId, insights) => {
    const shareText = `AI Insights:\n\n${insights}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'AI Insights',
          text: shareText
        });
      } catch (error) {
        console.log('Share cancelled or failed:', error);
      }
    } else {
      // Fallback: copy to clipboard
      try {
        await navigator.clipboard.writeText(shareText);
        alert('AI insights copied to clipboard!');
      } catch (error) {
        console.error('Failed to copy to clipboard:', error);
        // Final fallback: show in alert
        alert(shareText);
      }
    }
  };

  // Extract category from AI insights text
  const extractCategory = (insights) => {
    if (!insights) return null;
    const categoryMatch = insights.match(/Category:\s*([^\n]+)/i);
    if (categoryMatch) {
      const category = categoryMatch[1].trim();
      return CATEGORIES[category] ? category : 'Custom';
    }
    return null;
  };

  // Filter notes by selected category and search
  const getFilteredNotes = () => {
    let filtered = notes;
    
    // Apply search filter first
    if (searchQuery) {
      filtered = filtered.filter(note => {
        if (!note.text) return false;
        return note.text.toLowerCase().includes(searchQuery.toLowerCase());
      });
    }
    
    // Apply category filter
    if (selectedCategory !== 'All') {
      filtered = filtered.filter(note => {
        const insights = aiInsights[note.id];
        const category = extractCategory(insights);
        return category === selectedCategory;
      });
    }
    
    // Sort by timestamp (newest first)
    return filtered.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  };

  const handleSearch = (e) => {
    if (e.key === 'Enter') {
      setSearchQuery(searchTerm);
      console.log('Searching for:', searchTerm);
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
    setSearchQuery('');
  };

  return (
    <Authenticator
      components={{
        Header() {
          return (
            <div style={{ textAlign: 'center', padding: '2rem 1rem 1rem' }}>
              <h1 style={{ 
                color: '#3498db', 
                fontSize: '2rem', 
                marginBottom: '0.5rem',
                fontWeight: 'bold',
                fontStyle: 'italic'
              }}>
                Welcome to MyVoiceMe
              </h1>
              <p style={{ 
                color: '#666', 
                fontSize: '1.1rem', 
                lineHeight: '1.4',
                maxWidth: '400px',
                margin: '0 auto'
              }}>
                Speak naturally, get perfect notes + AI insights. Share anywhere, instantly.
              </p>
            </div>
          );
        },
      }}
      signUpAttributes={['given_name', 'family_name']}
      formFields={{
        signUp: {
          given_name: {
            label: 'First Name',
            placeholder: 'Enter your first name',
            isRequired: true,
            order: 1,
          },
          family_name: {
            label: 'Last Name', 
            placeholder: 'Enter your last name',
            isRequired: true,
            order: 2,
          },
          email: {
            order: 3,
          },
          password: {
            order: 4,
          },
          confirm_password: {
            order: 5,
          },
        },
        signIn: {
          username: {
            label: 'Email',
            placeholder: 'Enter your email address',
            defaultValue: (() => {
              const savedEmail = localStorage.getItem('lastEmail') || '';
              console.log('Loading saved email:', savedEmail);
              return savedEmail;
            })(),
          },
        },
      }}
    >
      {({ signOut, user }) => {
        // Call handler when user changes
        handleAuthUser(user);
        
        // Save email for next login
        if (user?.signInDetails?.loginId) {
          console.log('Saving email to localStorage:', user.signInDetails.loginId);
          localStorage.setItem('lastEmail', user.signInDetails.loginId);
        }
        
        return (
        <div className="app">
          <header className="header">
            <div className="header-top">
              <h1>MyVoiceMe</h1>
              <div className="user-info">
                <button 
                  onClick={() => setIsDarkMode(!isDarkMode)}
                  className="theme-toggle"
                  title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                >
                  {isDarkMode ? '☀️' : '🌙'}
                </button>
                <span>Welcome, {userFirstName}</span>
                <button onClick={signOut} className="sign-out-btn">Sign Out</button>
              </div>
            </div>
            <div className="search-bar">
              <div className="search-input-container">
                <input
                  type="text"
                  placeholder="Search notes... (Press Enter to search)"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={handleSearch}
                />
                {searchQuery && (
                  <button onClick={clearSearch} className="clear-search-btn">
                    ✕ Clear
                  </button>
                )}
              </div>
              {searchQuery && (
                <div style={{fontSize: '12px', color: '#666', marginTop: '5px'}}>
                  Searching for: "{searchQuery}" - Found {getFilteredNotes().length} notes
                </div>
              )}
            </div>
          </header>

      <div className="record-section">
        <button
          className={`record-btn ${isRecording ? 'recording' : ''}`}
          onClick={isRecording ? stopRecording : startRecording}
        >
          {isRecording ? '⏹️ Stop Recording' : '🎤 Start Recording'}
        </button>
        {isRecording && (
          <div className="recording-indicator">
            🔴 Recording... Speak now!
          </div>
        )}
        {liveTranscript && (
          <div className="live-transcript">
            <div className="transcript-label">Live Transcript:</div>
            <div className="transcript-text">{liveTranscript}</div>
          </div>
        )}
        
        <div className="debug-info">
          <small>
            Browser: {navigator.userAgent.includes('Chrome') ? 'Chrome' : 
                     navigator.userAgent.includes('Firefox') ? 'Firefox' : 
                     navigator.userAgent.includes('Safari') ? 'Safari' : 'Other'} | 
            MediaRecorder: {window.MediaRecorder ? '✅' : '❌'} | 
            Speech API: {window.SpeechRecognition || window.webkitSpeechRecognition ? '✅' : '❌'}
          </small>
          <button 
            onClick={loadNotesFromCloud} 
            className="sync-btn"
            style={{marginLeft: '10px', padding: '4px 8px', fontSize: '12px'}}
          >
            🔄 Sync
          </button>
        </div>
      </div>

      {microphoneError && (
        <div className="microphone-error" style={{
          background: '#fff3cd',
          border: '1px solid #ffeaa7',
          borderRadius: '8px',
          padding: '15px',
          margin: '10px 0',
          textAlign: 'center'
        }}>
          <div style={{ marginBottom: '10px', color: '#856404' }}>
            <strong>🎤 Microphone Issue Detected</strong>
          </div>
          <div style={{ fontSize: '14px', color: '#856404', marginBottom: '15px' }}>
            {microphoneError.name === 'NotAllowedError' 
              ? 'Microphone access was denied or blocked'
              : `Error: ${microphoneError.message}`
            }
          </div>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button 
              onClick={resetMicrophone}
              style={{
                background: '#3498db',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              🔄 Reset Microphone
            </button>
            <button 
              onClick={() => setMicrophoneError(null)}
              style={{
                background: '#95a5a6',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              ✕ Dismiss
            </button>
          </div>
          <div style={{ fontSize: '12px', color: '#856404', marginTop: '10px' }}>
            💡 On iOS Safari: Try refreshing the page or restarting Safari if issues persist
          </div>
        </div>
      )}

      {/* Category Filter Buttons */}
      <div className="category-filters">
        <button 
          className={`filter-btn ${selectedCategory === 'All' ? 'active' : ''}`}
          onClick={() => setSelectedCategory('All')}
        >
          All
        </button>
        {Object.entries(CATEGORIES).map(([category, config]) => (
          <button
            key={category}
            className={`filter-btn ${selectedCategory === category ? 'active' : ''}`}
            onClick={() => setSelectedCategory(category)}
          >
            {config.icon} {category.split(' ')[0]}
          </button>
        ))}
      </div>

      <div className="notes-list">
        {getFilteredNotes().map(note => {
          const category = extractCategory(aiInsights[note.id]);
          const categoryConfig = category ? CATEGORIES[category] : null;
          
          return (
          <div key={note.id} className="note-card">
            {/* Category Badge */}
            {categoryConfig && (
              <div 
                className="category-badge" 
                style={{ backgroundColor: categoryConfig.color }}
              >
                {categoryConfig.icon} {category}
              </div>
            )}
            <div className="note-header">
              <span className="timestamp">
                {new Date(note.timestamp).toLocaleString()}
              </span>
              <div className="note-actions">
                {note.syncing && <span className="sync-status syncing">⏳ Syncing...</span>}
                {note.synced && !note.syncing && <span className="sync-status synced">☁️ Synced</span>}
                {note.transcribing && <span className="sync-status transcribing">⏳ Enhancing transcript (20s)...</span>}
                {note.transcribeCompleted && <span className="sync-status transcribed">✨ Enhanced</span>}
                {note.syncError && (
                  <button 
                    onClick={() => retrySync(note)} 
                    className="sync-retry-btn"
                    title="Retry sync to cloud"
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#ff6b35',
                      cursor: 'pointer',
                      fontSize: '12px',
                      padding: '2px 4px'
                    }}
                  >
                    🔄 Tap to retry
                  </button>
                )}
                <button onClick={() => downloadMarkdown(note)} className="download-btn" title="Download as Markdown">
                  📝
                </button>
                <button 
                  onClick={() => {
                    console.log('🤖 AI BUTTON CLICKED!');
                    processWithAI(note);
                  }} 
                  className={`ai-btn ${aiProcessing[note.id] ? 'processing' : ''}`}
                  title={aiProcessing[note.id] ? 'Processing...' : 'AI Summary'}
                  disabled={aiProcessing[note.id]}
                >
                  {aiProcessing[note.id] ? '⏳' : '🤖'}
                </button>
                <button onClick={() => shareNote(note)} className="share-btn" title="Share via SMS/Apps">
                  📤
                </button>
                <button onClick={() => deleteNote(note.id)} className="delete-btn">
                  🗑️
                </button>
              </div>
            </div>
            <div className="note-text">
              {truncateTranscript(note.text, note.id)}
              {note.text && note.text.length > 400 && (
                <button 
                  className="transcript-toggle-btn"
                  onClick={(e) => toggleTranscriptExpansion(note.id, e)}
                  onTouchStart={(e) => e.stopPropagation()}
                  style={{
                    display: 'block',
                    marginTop: '8px',
                    padding: '4px 8px',
                    fontSize: '12px',
                    color: '#666',
                    background: 'none',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    WebkitTapHighlightColor: 'transparent'
                  }}
                >
                  {expandedTranscripts[note.id] ? 'Show less' : 'Show more'}
                </button>
              )}
            </div>
            {note.audioUrl && (
              <audio controls className="audio-player" preload="metadata">
                <source src={note.audioUrl} type="audio/mp4" />
                <source src={note.audioUrl} type="audio/webm" />
              </audio>
            )}
            {aiInsights[note.id] && (
              <div className="ai-insights-section">
                <div className="ai-insights-header">
                  <button 
                    className="ai-insights-toggle"
                    onClick={() => toggleAiInsights(note.id)}
                  >
                    🤖 AI Insights {expandedAiInsights[note.id] ? '▼' : '▶'}
                  </button>
                  {expandedAiInsights[note.id] && (
                    <button 
                      className="ai-share-btn"
                      onClick={() => shareAiInsights(note.id, aiInsights[note.id])}
                      title="Share AI Insights"
                    >
                      📤
                    </button>
                  )}
                </div>
                {expandedAiInsights[note.id] && (
                  <div className="ai-insights-content">
                    {aiInsights[note.id]}
                  </div>
                )}
              </div>
            )}
          </div>
        )})}
      </div>

      {getFilteredNotes().length === 0 && (
        <div className="empty-state">
          {searchQuery ? 'No notes found' : 'No notes yet. Tap record to start!'}
        </div>
      )}

      <SubscriptionModal 
        isOpen={showSubscriptionModal}
        onClose={() => setShowSubscriptionModal(false)}
        userEmail={currentUser?.signInDetails?.loginId}
      />

      <footer className="app-footer">
        <div className="footer-links">
          {/* <button 
            className="upgrade-btn"
            onClick={() => setShowSubscriptionModal(true)}
          >
            Start Free Trial
          </button> */}
          <a href="/terms.html" target="_blank">Terms of Service</a>
          <a href="/privacy.html" target="_blank">Privacy Policy</a>
          <a href="mailto:support@myvoiceme.com">Contact Support</a>
        </div>
        <div className="footer-text">
          © 2025 MyVoiceMe. Professional voice-to-text notes.
        </div>
      </footer>
    </div>
        );
      }}
    </Authenticator>
  );
}

export default App;
